/**
 * Dashboard inline widgets, extracted from /pages/Dashboard.js so the
 * page-level file can stay shell-thin (header + tab strip + tab body
 * delegation). Each widget is self-contained, owns its own data fetch
 * where applicable, and accepts only the props it actually needs.
 *
 * History: before Feb 2026 these all lived inline in Dashboard.js
 * (which had ballooned to ~1280 LOC). Splitting them out unblocks the
 * per-tab refactor and makes individual widgets independently testable.
 */
import { useState, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Brain, GraduationCap, Clock, Calendar, Flame, Trophy, BookMarked,
  ChevronRight, RotateCcw, Sparkles, Crown, AlertTriangle, FolderOpen,
  FileText, Download, Upload, Loader2, Lightbulb, Target,
} from 'lucide-react';
import { Button } from '../ui/button';
import { authFetch } from '../../utils/authFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Glass Progress Bar ──────────────────────────────── */
export const ProgressBar = ({ current, limit, color, label }) => {
  const isUnlimited = limit === -1 || limit === 'unlimited';
  const percentage = isUnlimited ? 100 : Math.min((current / limit) * 100, 100);
  const isNearLimit = !isUnlimited && percentage >= 80;
  const isAtLimit = !isUnlimited && percentage >= 100;

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-center text-sm font-body">
        <span className="text-[var(--isp-body)] text-xs">{label}</span>
        <span className={`font-semibold text-xs ${isAtLimit ? 'text-red-600' : isNearLimit ? 'text-amber-600' : 'text-[var(--isp-heading)]'}`}>
          {isUnlimited ? 'Unlimited' : `${current}/${limit}`}
        </span>
      </div>
      <div className="h-2 bg-white/10 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className={`h-full rounded-full ${
            isUnlimited ? 'bg-gradient-to-r from-emerald-400 to-green-500' :
            isAtLimit ? 'bg-gradient-to-r from-red-500 to-rose-500' :
            isNearLimit ? 'bg-gradient-to-r from-amber-400 to-orange-500' :
            `bg-gradient-to-r ${color}`
          }`}
        />
      </div>
    </div>
  );
};

/* ─── Streak Widget ────────────────────────────────── */
export const StudyStreakWidget = ({ data }) => {
  if (!data) return null;
  const { current_streak, longest_streak, is_active_today, message } = data;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.1 }}
      className="isp-card p-5 lg:p-6"
      data-testid="study-streak-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-[#FF3B30]">
            <Flame className="w-4 h-4 text-white" aria-hidden />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-[var(--isp-heading)] text-sm">Study Streak</h3>
            <p className="font-body text-[10px] text-[var(--isp-body)]">{message}</p>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-5">
        <div data-testid="streak-current-tile">
          <div className="flex items-center gap-2">
            <Flame className={`w-5 h-5 ${current_streak > 0 ? 'text-orange-500' : 'text-gray-300'}`} aria-hidden />
            <div className="wave-stat">
              <span className={`wave-stat__value ${current_streak > 0 ? '!text-orange-500' : '!text-gray-300'}`}>
                {current_streak}
              </span>
              <span className="wave-stat__unit">days</span>
            </div>
          </div>
          <p className="text-[10px] font-body text-[var(--isp-body)] mt-1 uppercase tracking-[0.14em]">Current</p>
        </div>
        <div data-testid="streak-best-tile">
          <div className="flex items-center gap-2">
            <Trophy className={`w-4 h-4 ${longest_streak > 0 ? 'text-[#FFD700]' : 'text-gray-300'}`} aria-hidden />
            <div className="wave-stat">
              <span className={`wave-stat__value ${longest_streak > 0 ? '' : '!text-gray-300'}`}>
                {longest_streak}
              </span>
              <span className="wave-stat__unit">days</span>
            </div>
          </div>
          <p className="text-[10px] font-body text-[var(--isp-body)] mt-1 uppercase tracking-[0.14em]">Best</p>
        </div>
        <div className="ml-auto">
          {is_active_today ? (
            <span className="px-3 py-1.5 rounded-full bg-emerald-500/20 text-emerald-400 text-xs font-body font-medium">Active today</span>
          ) : (
            <span className="px-3 py-1.5 rounded-full bg-orange-500/20 text-orange-400 text-xs font-body font-medium">Study today!</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/* ─── Due Reviews Widget ───────────────────────────── */
// Reads from the shared TanStack Query cache. `stats` prop is back-compat:
// when omitted we self-fetch (sharing the cache with CardsDueTodayWidget,
// so only one /srs/stats network call happens on dashboard load).
export const DueReviewsWidget = ({ stats: statsProp }) => {
  const { data: statsQ } = useQuery({
    queryKey: ['srs-stats'],
    queryFn: async () => (await axios.get(`${API}/srs/stats`)).data,
    staleTime: 60_000,
    enabled: !statsProp,
  });
  const stats = statsProp || statsQ;
  if (!stats) return null;
  const dueCount = stats.due_now || 0;
  const mastered = stats.mastered || 0;
  const total = stats.total_items || 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.15 }}
      className="isp-card p-5 lg:p-6"
      data-testid="due-reviews-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600">
            <BookMarked className="w-4 h-4 text-white" aria-hidden />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-[var(--isp-heading)] text-sm">Due Reviews</h3>
            <p className="font-body text-[10px] text-[var(--isp-body)]">Spaced repetition</p>
          </div>
        </div>
        <Link to="/review">
          <Button size="sm" variant="ghost" className="rounded-xl text-xs text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] font-body" data-testid="review-link-btn">
            Review <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </div>
      {dueCount > 0 ? (
        <div className="space-y-3">
          <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center">
              <span className="text-lg font-heading font-bold text-amber-400">{dueCount}</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-body font-medium text-[var(--isp-heading)]">Cards due for review</p>
              <p className="text-xs font-body text-[var(--isp-body)]">Review now to strengthen memory</p>
            </div>
          </div>
          <Link to="/review">
            <Button className="w-full  gap-2 isp-btn-primary font-body font-medium" data-testid="start-review-dashboard-btn">
              <RotateCcw className="w-4 h-4" /> Start Review
            </Button>
          </Link>
        </div>
      ) : total > 0 ? (
        <div className="text-center py-3">
          <div className="w-10 h-10 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-2">
            <Sparkles className="w-5 h-5 text-emerald-400" aria-hidden />
          </div>
          <p className="text-sm font-body font-medium text-emerald-400">All caught up!</p>
          <p className="text-xs font-body text-[var(--isp-body)] mt-1">{mastered}/{total} items mastered</p>
        </div>
      ) : (
        <div className="text-center py-3">
          <p className="text-sm font-body text-[var(--isp-body)]">No review items yet</p>
          <p className="text-xs font-body text-[var(--isp-body)]/60 mt-1">Generate flashcards from lectures</p>
        </div>
      )}
    </motion.div>
  );
};

/* ─── AI Recommendations Widget ───────────────────── */
export const AIRecommendationsWidget = ({ recommendations }) => {
  if (!recommendations || recommendations.length === 0) return null;

  const getIcon = (type) => {
    const icons = { optimal_time: Clock, weak_area: Target, exam_prep: Calendar, spaced_repetition: BookMarked, streak_bonus: Flame };
    const Icon = icons[type] || Lightbulb;
    return <Icon className="w-4 h-4" aria-hidden />;
  };
  const getColors = (priority) => {
    if (priority === 'high') return 'border-red-500/20 bg-red-500/10';
    if (priority === 'medium') return 'border-amber-500/20 bg-amber-500/10';
    return 'border-emerald-500/20 bg-emerald-500/10';
  };
  const getIconColor = (type) => {
    const colors = { optimal_time: 'text-blue-400', weak_area: 'text-red-400', exam_prep: 'text-amber-400', spaced_repetition: 'text-purple-400', streak_bonus: 'text-orange-400' };
    return colors[type] || 'text-cyan-400';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.3 }}
      className="isp-card p-5 lg:p-6"
      data-testid="ai-recommendations-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5">
          <div className="p-2 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
            <Sparkles className="w-4 h-4 text-white" aria-hidden />
          </div>
          <h3 className="font-heading font-semibold text-[var(--isp-heading)] text-sm">AI Recommendations</h3>
        </div>
        <Link to="/recommendations">
          <Button size="sm" variant="ghost" className="rounded-xl text-xs text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)] font-body">
            All <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </Link>
      </div>
      <div className="space-y-2">
        {recommendations.slice(0, 3).map((rec, i) => (
          <motion.div
            key={`item-${i}`}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.08 }}
            className={`p-3 rounded-xl border ${getColors(rec.priority)} transition-all hover:-translate-y-0.5 hover:shadow-md`}
          >
            <div className="flex items-start gap-2.5">
              <div className={`p-1.5 rounded-lg bg-[var(--isp-glass-inner)] ${getIconColor(rec.type)}`}>{getIcon(rec.type)}</div>
              <div className="flex-1 min-w-0">
                <h4 className="font-body text-sm font-medium text-[var(--isp-heading)] truncate">{rec.title}</h4>
                <p className="font-body text-[var(--isp-body)] text-xs mt-0.5 line-clamp-2">{rec.description}</p>
              </div>
              {rec.action_url && (
                <Link to={rec.action_url} aria-label="Open recommendation"><ChevronRight className="w-4 h-4 text-[var(--isp-body)]/40 hover:text-[var(--isp-heading)] transition-colors" /></Link>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

/* ─── Usage Meter Widget ──────────────────────────── */
export const UsageMeterWidget = ({ subscription, onUpgrade }) => {
  if (!subscription) return null;
  const { usage, plan } = subscription;
  const isPaid = plan !== 'free';
  const storage = usage?.storage;

  const formatStorage = (mb) => {
    if (!mb && mb !== 0) return '0';
    if (mb >= 1024) return `${(mb / 1024).toFixed(1)}GB`;
    if (mb >= 1) return `${mb.toFixed(1)}MB`;
    return `${Math.round(mb * 1024)}KB`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
      className="isp-card p-5 lg:p-6"
      data-testid="usage-meter-widget"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div className={`p-2.5 rounded-xl ${isPaid ? 'bg-gradient-to-br from-emerald-500 to-green-600' : 'bg-gradient-to-br from-[var(--isp-accent)] to-emerald-600'}`}>
            {isPaid ? <Crown className="w-4 h-4 text-white" aria-hidden /> : <Sparkles className="w-4 h-4 text-white" aria-hidden />}
          </div>
          <div>
            <h3 className="font-heading font-semibold text-[var(--isp-heading)] text-sm">Usage Meter</h3>
            <p className="font-body text-[10px] text-[var(--isp-body)] capitalize">{plan} Plan</p>
          </div>
        </div>
        {!isPaid && onUpgrade && (
          <Button size="sm" className="rounded-xl text-xs bg-[#FFD700] text-[#002147] hover:bg-[#F6D000] font-body font-semibold shadow-sm" data-testid="upgrade-button" onClick={onUpgrade}>
              Upgrade
            </Button>
        )}
      </div>
      <div className="space-y-3">
        <ProgressBar current={usage?.ai_requests?.used_today || 0} limit={usage?.ai_requests?.limit} color="from-cyan-400 to-blue-500"
          label={<span className="flex items-center gap-1.5"><Brain className="w-3 h-3" aria-hidden /> AI Requests</span>} />
        <ProgressBar current={usage?.lectures?.current || 0} limit={usage?.lectures?.limit} color="from-pink-400 to-rose-500"
          label={<span className="flex items-center gap-1.5"><GraduationCap className="w-3 h-3" aria-hidden /> Lectures</span>} />
        <ProgressBar current={usage?.subjects?.current || 0} limit={usage?.subjects?.limit} color="from-violet-400 to-purple-500"
          label={<span className="flex items-center gap-1.5"><FolderOpen className="w-3 h-3" aria-hidden /> Subjects</span>} />
      </div>

      {/* Storage Usage Bar */}
      {storage && (
        <div className="mt-4 pt-3 border-t border-[var(--isp-divider)]" data-testid="storage-usage-bar">
          <div className="space-y-1.5">
            <div className="flex justify-between items-center text-sm font-body">
              <span className="text-[var(--isp-body)] text-xs flex items-center gap-1.5">
                <FolderOpen className="w-3 h-3" aria-hidden /> Storage
              </span>
              <span className={`font-semibold text-xs ${
                storage.percentage >= 100 ? 'text-red-600' :
                storage.percentage >= 80 ? 'text-amber-600' :
                'text-[var(--isp-heading)]'
              }`}>
                {formatStorage(storage.used_mb)} / {formatStorage(storage.limit_mb)}
              </span>
            </div>
            <div className="h-2.5 bg-white/10 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(storage.percentage, 100)}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
                className={`h-full rounded-full ${
                  storage.percentage >= 100 ? 'bg-gradient-to-r from-red-500 to-rose-500' :
                  storage.percentage >= 80 ? 'bg-gradient-to-r from-amber-400 to-orange-500' :
                  'bg-gradient-to-r from-emerald-400 to-green-500'
                }`}
              />
            </div>
            <div className="flex justify-between text-[10px] font-body text-[var(--isp-body)]">
              <span>{storage.documents || 0} docs, {storage.audio_files || 0} audio</span>
              <span>{formatStorage(storage.remaining_mb)} free</span>
            </div>
          </div>
          {storage.percentage >= 80 && !isPaid && onUpgrade && (
            <button
              onClick={onUpgrade}
              className="mt-2 w-full text-xs font-body font-medium text-center py-1.5 rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors"
              data-testid="storage-upgrade-prompt"
            >
              {storage.percentage >= 100 ? 'Storage full — Upgrade for more space' : 'Running low on storage — Upgrade now'}
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};

/* ─── Progress Report Widget ──────────────────────── */
export const ProgressReportWidget = () => {
  const [downloading, setDownloading] = useState(false);

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const response = await authFetch(`${API}/reports/progress-pdf`);
      if (!response.ok) throw new Error('Failed to generate report');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `iStudyPRO_Progress_Report.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      // Silently fail
    } finally {
      setDownloading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
      className="p-4  bg-[var(--isp-glass)] border border-[var(--isp-glass-border)] backdrop-blur-lg"
      data-testid="progress-report-widget"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-600">
          <FileText className="w-4 h-4 text-white" aria-hidden />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-[var(--isp-heading)] text-sm">Progress Report</h3>
          <p className="font-body text-[10px] text-[var(--isp-body)]">Download your study summary</p>
        </div>
      </div>
      <p className="text-xs text-[var(--isp-body)] mb-3 leading-relaxed">
        Get a PDF with your subjects, exam scores, flashcard stats, streaks, badges, and study activity.
      </p>
      <Button
        onClick={downloadReport}
        disabled={downloading}
        className="w-full rounded-xl gap-2 bg-[#0A0A0A] text-white hover:from-emerald-500 hover:to-teal-500 text-xs font-body"
        data-testid="download-progress-report-btn"
      >
        <Download className="w-3.5 h-3.5" />
        {downloading ? 'Generating PDF...' : 'Download Progress Report'}
      </Button>
    </motion.div>
  );
};

/* ─── Subscription Banner ─────────────────────────── */
export const SubscriptionBanner = ({ subscription, user, onUpgrade }) => {
  // Hide whenever the user is a paid subscriber, ambassador, or on any
  // non-'free' plan. Defensive double-check via AuthContext `user` because
  // the local `subscription` object can be stale right after a Stripe
  // checkout — the user's plan flips on the backend before the dashboard
  // re-fetches /subscription/status.
  if (!subscription) return null;
  const userPlan = (user?.subscription_plan || '').toLowerCase();
  const userStatus = (user?.subscription_status || '').toLowerCase();
  const userIsPaid =
    userStatus === 'active' &&
    ['pro', 'premium', 'student', 'basic'].includes(userPlan);
  const ambassadorActive = (() => {
    const until = user?.ambassador_access_until;
    if (!until) return false;
    try { return new Date(until) > new Date(); } catch { return false; }
  })();
  if (subscription.plan !== 'free' || userIsPaid || ambassadorActive) return null;
  const usageWarnings = [];
  if (!subscription.usage?.lectures?.can_create) usageWarnings.push(`Lectures: ${subscription.usage.lectures.current}/${subscription.usage.lectures.limit}`);
  if (!subscription.usage?.subjects?.can_create) usageWarnings.push(`Subjects: ${subscription.usage.subjects.current}/${subscription.usage.subjects.limit}`);
  if (subscription.usage?.storage?.percentage >= 90) usageWarnings.push(`Storage: ${subscription.usage.storage.percentage}% used`);
  const hasWarnings = usageWarnings.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-[20px] p-4 border ${
        hasWarnings ? 'bg-amber-500/10 border-amber-500/20' : 'bg-[var(--isp-glass-inner)] border-[var(--isp-glass-border)]'
      } backdrop-blur-sm`}
      data-testid="subscription-banner"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-start gap-3">
          {hasWarnings ? <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" aria-hidden /> : <Crown className="w-5 h-5 text-[var(--isp-accent)] flex-shrink-0 mt-0.5" aria-hidden />}
          <div>
            <p className="font-heading font-semibold text-[var(--isp-heading)] text-sm">
              {hasWarnings ? "You've reached your free plan limits" : "You're on the Free Plan"}
            </p>
            <p className="font-body text-[var(--isp-body)] text-xs mt-0.5">
              {hasWarnings ? `${usageWarnings.join(' | ')}. Upgrade for unlimited access!` : `${subscription.usage?.ai_requests?.remaining || 0} AI requests remaining today`}
            </p>
          </div>
        </div>
        {onUpgrade && (
          <Button className=" gap-2 isp-btn-primary font-body font-semibold" data-testid="upgrade-now-btn" onClick={onUpgrade}>
            <Crown className="w-4 h-4" /> Upgrade Now
          </Button>
        )}
      </div>
    </motion.div>
  );
};

/* ─── Quick Upload Widget ─────────────────────────── */
export const QuickUploadWidget = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/uploads/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadResult({ success: true, name: file.name, id: res.data.id || res.data.filename });
    } catch (err) {
      setUploadResult({ success: false, error: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleUpload(file);
  };

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mb-6"
    >
      <div
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
        onClick={() => !uploading && fileRef.current?.click()}
        className={`relative cursor-pointer  border-2 border-dashed p-6 text-center transition-all ${
          isDragging
            ? 'border-[#002FA7] bg-[#002FA7]/5 scale-[1.01]'
            : uploadResult?.success
            ? 'border-[#22C55E]/40 bg-[#22C55E]/5'
            : 'border-[var(--isp-glass-border)] bg-[var(--isp-glass)] hover:border-[#002FA7]/40 hover:bg-[#002FA7]/5'
        }`}
        data-testid="quick-upload-zone"
      >
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.txt,.md,.csv,.doc,.docx,.pptx,.png,.jpg,.jpeg"
          onChange={onFileSelect}
          className="hidden"
          data-testid="quick-upload-input"
          aria-label="Upload a document"
        />

        {uploading ? (
          <div className="flex items-center justify-center gap-3">
            <Loader2 className="w-6 h-6 animate-spin text-[#002FA7]" aria-hidden />
            <span className="text-sm font-medium text-[var(--isp-heading)]">Uploading...</span>
          </div>
        ) : uploadResult?.success ? (
          <div className="flex items-center justify-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#22C55E]/15 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#22C55E]" aria-hidden />
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-[var(--isp-heading)]">&ldquo;{uploadResult.name}&rdquo; uploaded!</p>
              <Link to="/document-uploads" className="text-xs text-[#002FA7] hover:underline">View in My Documents</Link>
            </div>
          </div>
        ) : uploadResult?.error ? (
          <div className="flex items-center justify-center gap-3">
            <p className="text-sm text-red-500">{uploadResult.error}</p>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#002FA7]/10 flex items-center justify-center">
              <Upload className="w-5 h-5 text-[#002FA7]" aria-hidden />
            </div>
            <div className="text-center sm:text-left">
              <p className="text-sm font-semibold text-[var(--isp-heading)]">Quick Upload</p>
              <p className="text-xs text-[var(--isp-muted)]">Drag & drop a document, slide, or PDF here — or click to browse</p>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};
